"use client";

import React, { JSX } from "react";

import { useLocale, useTranslations } from "next-intl";

import { useRealtorPropertyDetails } from "@/hooks/useRealtorPropertyDetails";
import CalendarIcon from "@/icons/contacts/calendar.svg";
import FileIcon from "@/icons/contacts/file.svg";
import Loader from "@/ui/Loader";
import { cn } from "@/utils/cn";
import { RealtorWrapper } from "@/widgets/Realtor/RealtorWrapper";

import FieldSection from "./components/FieldSection";
import PhoneDisplay from "./components/PhoneDisplay";
import styles from "./style.module.scss";
import { IRealtorPropertyDetailsProps } from "./types";
import { formatDate, getStatusDisplay, truncateUrl } from "./utils/fieldUtils";
import { DealType } from "@/enums/dealType";
import OwnerContacts from "@/widgets/RealtorPropertyDetails/components/OwnerContacts";
import { hasValue } from "@/utils/hasValue";

export const RealtorPropertyDetails: React.FC<IRealtorPropertyDetailsProps> = ({
  propertyId,
  dealType,
  property,
  isLoading = false,
  className,
}) => {
  const t = useTranslations();
  const tRealtor = useTranslations("realtor");
  const tProperty = useTranslations("PropertyOptions");
  const tAdditional = useTranslations("AdditionalFilters");
  const tAddObject = useTranslations("AddObjectForm");
  const locale = useLocale();
  const isRTL = locale === "ar";

  // Use the service-based hook with property data
  const {
    data: processedData,
    isLoading: isDataLoading,
    error: dataError,
  } = useRealtorPropertyDetails(
    propertyId,
    dealType || property?.dealType,
    property,
    {
      enabled: true,
      fetchUsers: false, // Disable user fetching to avoid CORS
    },
  );

  // Loading state
  if (isLoading || isDataLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader />
        <p className={styles.loadingText}>{t("loading")}...</p>
      </div>
    );
  }

  // Always use property data with proper fallbacks
  const propertyData = processedData?.property;

  // Create safe structure with all required fields
  const safePropertyDetails = {
    ...propertyData,
    // Required fields with fallbacks
    creatorFields: propertyData?.creatorFields || {
      createdDate: propertyData?.createdAt || new Date(),
      createdBy: propertyData?.creatorId || null,
      responsible: propertyData?.assignedTo || null,
    },
    updatedAt: propertyData?.updatedAt || new Date(),
    customId: propertyData?.realtorFields?.exclusiveNumber,

    // Project info
    projectInfo: propertyData?.projectInfo || {
      constructionStatus: propertyData?.constructionStatus,
    },

    // URLs and links
    urls: propertyData?.urls || [],
    realtorFields: propertyData?.realtorFields || {
      customAdLinks: [],
    },

    // Preserve any additional data
  };

  const notPrimary = safePropertyDetails.dealType !== DealType.PRIMARY;

  // Mock data for creator and responsible since we disabled user fetching to avoid CORS
  const creator = processedData?.creator || null;
  const responsible = processedData?.responsible || null;

  // Handle phone click
  const handlePhoneClick = (phone: string, source: "object" | "creator") => {
    console.log(`Phone clicked: ${phone} from ${source}`);
    // Implement phone reveal logic here
  };

  // Custom renderers
  const renderDate = (date: Date | string) => {
    return (
      <div className="flex gap-[12px] items-center">
        <CalendarIcon />
        <span>{formatDate(date)}</span>
      </div>
    );
  };

  const renderUser = (user: any, fallbackName: string) =>
    user ? `${user.firstName} ${user.lastName}` : fallbackName;

  const renderStatus = (status?: string) => getStatusDisplay(status || "", t);

  const renderUrl = (url: string) => (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-[#323232] text-[16px] underline"
    >
      {truncateUrl(url)}
    </a>
  );

  const renderListUrls = (urls: string[]) => {
    if (!urls?.length) return null;

    return (
      <div className="flex flex-col gap-[12px] items-start">
        {urls.map((url, index) => (
          <React.Fragment key={`${url}-${index}`}>
            {renderUrl(url)}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Real document renderer - shows actual files from projectInfo.files
  const renderDocuments = () => {
    const files = safePropertyDetails.projectInfo?.files || [];

    if (files.length === 0) {
      return null;

      // return (
      //   <div className={styles.fieldValue}>
      //     <span className={styles.emptyValue}>{t("realtor.noDocuments")}</span>
      //   </div>
      // );
    }

    return (
      <div className="space-y-2">
        {files.map((file: any, index: number) => (
          <div
            key={index}
            className="flex gap-[12px] items-center"
          >
            <FileIcon />
            <span className={styles.documentText}>
              {file.name || file.filename || `${t("document")} ${index + 1}`}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Real call status renderer - uses actual data
  const renderCallStatusRating = () => {
    const callDate = safePropertyDetails.realtorFields?.callDate;
    const rating = safePropertyDetails.realtorFields?.rating || 0;

    return (
      <div className={styles.statusRating}>
        <div className={styles.statusDots}>
          {[1, 2, 3, 4, 5, 6, 7].map((dot) => (
            <div
              key={dot}
              className={cn(
                styles.statusDot,
                dot <= rating && styles.statusDotActive,
              )}
            />
          ))}
        </div>
        <span className={styles.statusDate}>
          {callDate ? formatDate(callDate) : t("realtor.noCallDate")}
        </span>
      </div>
    );
  };

  const detailsFields = [
    {
      labelKey: "realtor.detailsCreatedDate",
      value: safePropertyDetails.creatorFields.createdDate,
      renderValue: renderDate,
      key: "createdDate",
    },
    {
      labelKey: "realtor.detailsLastUpdated",
      value: safePropertyDetails.updatedAt,
      renderValue: renderDate,
      key: "lastUpdated",
    },
    {
      labelKey: "realtor.detailsCreatedBy",
      value: creator,
      renderValue: (user) => renderUser(user, t("realtor.userUnknownFallback")),
      key: "createdBy",
    },
    {
      labelKey: "realtor.detailsAssignedTo",
      value: responsible,
      renderValue: (user) => renderUser(user, t("realtor.userUnknownFallback")),
      key: "assignedTo",
    },
  ];

  const projectFields = [
    notPrimary && {
      labelKey: "realtor.detailsStatus",
      value:
        safePropertyDetails.realtorFields?.isExclusive ? "statusExclusive" : null,
      renderValue: tRealtor,
      key: "status",
    },
    notPrimary && {
      labelKey: "realtor.detailsExclusiveNumber",
      value: safePropertyDetails.customId,
      key: "exclusiveNumber",
    },
    notPrimary && {
      labelKey: "AddObjectForm.Owner type",
      value:
        safePropertyDetails.realtorFields.isAgent
          ? "Agent"
          : "Owner",
      renderValue: tAddObject,
      key: "ownerType",
    },
    safePropertyDetails.realtorFields?.tags?.length > 0 && {
      labelKey: "AddObjectForm.Tags",
      value: safePropertyDetails.realtorFields.tags,
      renderValue: () => {
        return safePropertyDetails.realtorFields?.tags?.map((i) => (
          <p key={i}>{tAddObject(`tags.${i}`)}</p>
        ));
      },
      key: "tags",
    },
    // Документов пока что нет
    // {
    //   labelKey: "realtor.detailsExclusiveDocs",
    //   value: safePropertyDetails.projectInfo?.files || null,
    //   renderValue: renderDocuments,
    //   key: "documents",
    // },
  ].filter(Boolean);

  const linksFields = [
    {
      labelKey: "realtor.detailsOriginalLink",
      value: safePropertyDetails.realtorFields.originalListingUrl,
      renderValue: renderUrl,
      key: "originalLink",
    },
    {
      labelKey: "realtor.detailsAdLink",
      value: safePropertyDetails.realtorFields.customAdLinks,
      renderValue: renderListUrls,
      key: `adLink`,
    },
  ];

  const callFields = [
    {
      labelKey: "realtor.detailsCallDate",
      value: safePropertyDetails.realtorFields?.callDate,
      renderValue: renderDate,
      key: "callDate",
    },
    {
      labelKey: "realtor.callStatus",
      value: safePropertyDetails.realtorFields?.callStatus,
      renderValue: tProperty,
      key: "callStatusText",
    },
  ];

  const aboutFields = [
    {
      labelKey: "realtor.detailsAboutClient",
      value: safePropertyDetails.realtorFields?.comments,
      key: "clientNote",
    },
  ];

  const renderSectionIfHasData = (fields: any[], render: () => JSX.Element) => {
    const validFields = fields.filter((field) => hasValue(field.value));
    return validFields.length ? render() : null;
  };

  return (
    <RealtorWrapper className={cn(styles.realtorPropertyDetails, className)}>
      {/* Section title above the card, on gray background */}
      <h2
        className={cn(
          "object-detail__section-header",
          styles.sectionHeader,
          "mb-[3rem]",
        )}
      >
        {tRealtor("detailsTitle")}
      </h2>

      {/* Pixel-perfect card container matching Figma: 1653.75px × 497px */}
      <div
        className={styles.detailsCard}
        dir={isRTL ? "rtl" : "ltr"}
      >
        {/* Main content with 6-column layout */}
        <div className={styles.contentContainer}>
          <div className={styles.gridContainer}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[24px] md:gap-[16px] relative">
              {renderSectionIfHasData(detailsFields, () => (
                <div className={styles.section}>
                  <FieldSection
                    title={tRealtor("detailsMainInfo")}
                    fields={detailsFields}
                    data-testid="main-info-section"
                  />
                </div>
              ))}

              {renderSectionIfHasData(projectFields, () => (
                <div className={styles.section}>
                  <FieldSection
                    title={tRealtor("detailsProjectStatus")}
                    fields={projectFields}
                    data-testid="project-status-section"
                  />
                </div>
              ))}

              {renderSectionIfHasData(linksFields, () => (
                <div className={styles.section}>
                  <FieldSection
                    title={tRealtor("detailsLinks")}
                    fields={linksFields}
                    data-testid="links-section"
                  />
                </div>
              ))}

              {renderSectionIfHasData(callFields, () => (
                <div className={styles.section}>
                  <FieldSection
                    title={tRealtor("detailsCallStatus")}
                    fields={callFields}
                    data-testid="call-status-section"
                  />
                </div>
              ))}

              {renderSectionIfHasData(aboutFields, () => (
                <div
                  className={cn(
                    styles.section,
                    "md:col-span-2 whitespace-normal break-words [overflow-wrap:anywhere]",
                  )}
                >
                  <FieldSection
                    title={tRealtor("detailsNotes")}
                    fields={aboutFields}
                    data-testid="notes-section"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </RealtorWrapper>
  );
};

export default RealtorPropertyDetails;
